import React, { createContext, useEffect, useState } from 'react';

// third-party
import jwtDecode from 'jwt-decode';

// project imports
import Loader from 'ui-component/Loader';
import axios from 'utils/axios';

// types
import { KeyedObject } from 'types';
import { JWTContextType } from 'types/auth';

import { useDispatch, persister, useSelector } from 'store';
import { accountLogin, accountLogout } from '../store/state/data-slices/accountSlice';
import { openSnackbar } from 'store/state/data-slices/snackbar';
import { useLocation, useNavigate } from 'react-router-dom';
import useQuery from 'hooks/useQuery';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

const getCsrfToken = async () => {
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    const fingerprint = result.visitorId;

    localStorage.setItem('fp_id', fingerprint);

    await axios({
        url: '/auth/_csurf',
        method: 'post',
        data: {
            fingerprint
        },
        withCredentials: true
    });


    const res = await axios({
        url: '/auth/csrf-token',
        method: 'get',
        headers: {
            'x-fingerprint-id': fingerprint
        },
        withCredentials: true
    });

    const { csrfToken } = res?.data;
    localStorage.setItem('csrf_token', csrfToken);
};

const tokenKey = process.env.REACT_APP_COOKIES_AUTH_KEY || '';
const verifyToken: (st: string) => boolean = (serviceToken) => {
    if (!serviceToken) {
        localStorage.removeItem('serviceToken');
        return false;
    }
    const decoded: KeyedObject = jwtDecode(serviceToken);
    /**
     * Property 'exp' does not exist on type '<T = unknown>(token: string, options?: JwtDecodeOptions | undefined) => T'.
     */
    return decoded.exp > Date.now() / 1000;
};

export const setSession = (serviceToken?: string | null) => {
    if (serviceToken && tokenKey) {
        localStorage.setItem(tokenKey, serviceToken);
        axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    } else {
        localStorage.removeItem(tokenKey);

    }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //
const JWTContext = createContext<JWTContextType | null>(null);

export const JWTProvider = ({ children }: { children: React.ReactElement }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const getQuery = useQuery();
    const path = location.pathname;
    const state = useSelector((state) => state.account);
    const [accountType, setAccountType] = useState('');
    const serviceToken = window.localStorage.getItem(tokenKey);
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    const fp_id = window.localStorage.getItem('fp_id');
    axios.defaults.headers.common['x-fingerprint-id'] = fp_id || '';
    axios.defaults.headers.common['x-csrf-token'] = window.localStorage.getItem('csrf_token') || '';
    setSession(serviceToken);

    useEffect(() => {
        const init = async () => {
            try {
                await getCsrfToken();
                const fp_id = window.localStorage.getItem('fp_id');
                axios.defaults.headers.common['x-fingerprint-id'] = fp_id || '';
                axios.defaults.headers.common['x-csrf-token'] = window.localStorage.getItem('csrf_token') || '';
                if (path === '/admin-auth') {
                    const token: any = getQuery.get('token');
                    const verify = verifyToken(token);
                    if (verify) {
                        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
                        const response = await axios.get(`/users/auth/me`);
                        const { user } = response.data;
                        if (user) {
                            setSession(token);
                            dispatch(accountLogin({ isLoggedIn: true, user, accountType }));
                        }
                    }
                } else {
                    if (serviceToken && verifyToken(serviceToken)) {
                        const response = await axios({
                            url: `/users/auth/me`,
                            method: 'get',
                            data: {}
                        });
                        const { user } = response.data;
                        dispatch(accountLogin({ isLoggedIn: true, user, accountType }));
                    } else {
                        await axios.post(`/auth/logout`);
                        setSession(null);
                        persister.purge();
                        dispatch(accountLogout({}));
                    }
                }

            } catch (err) {
                console.error(err);
                dispatch(accountLogout({}));
            }
        };

        init();
    }, []);

    const adminLogin = async (type: string, typeOfMessage: string, email: string, password: string, mobile: number, verifyCode: string, isLoginWithCode: number) => {
        const isEmail = type === 'email';
        const obj: any = { type, password, verifyCode, isLoginWithCode, typeOfMessage };
        if (isEmail) obj.email = email;
        if (!isEmail) obj.mobile = mobile;
        const currentHost = window.location.host;
        const isAdmin = currentHost.startsWith('app.');
        const response = await axios.post(isAdmin ? `/auth/admin/login` : `/auth/login`, obj);
        const { token, user } = response.data.data;
        setSession(token);
        dispatch(accountLogin({ isLoggedIn: true, user, accountType }));
    };

    const register = async (email: string, password: string, name: string, mobile: string, verifyCodeEmail: string, verifyCodeMobile: string) => {
        const response = await axios.post('/auth/register', {
            email,
            password,
            name,
            mobile,
            verifyCodeEmail,
            verifyCodeMobile,
        });

        if (response.data && response.data.success) {
            const { token } = response.data.data;
            setSession(token);
            const userMe = await axios.get(`/users/auth/me`);
            const { user } = userMe.data;
            dispatch(accountLogin({ isLoggedIn: true, user, accountType }));
            dispatch(
                openSnackbar({
                    open: true,
                    message: response.data.message,
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
            navigate('register', { replace: true });
        } else {
            dispatch(
                openSnackbar({
                    open: true,
                    message: response.data.message,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }

    };

    const logout = async (isAll?: boolean) => {
        setSession(null);
        persister.purge();
        persister.flush();
        dispatch(accountLogout({}));
        (await axios.post(`/auth/logout`, { isAll: isAll ? isAll : false }));

    };

    const resetPassword = (email: string) => {

    };

    const updateProfile = () => { };

    if (state.isInitialized !== undefined && !state.isInitialized) {
        return <Loader />;
    }

    return (
        <JWTContext.Provider value={{ ...state, adminLogin, logout, register, resetPassword, updateProfile }}>{children}</JWTContext.Provider>
    );
};

export default JWTContext;
